import { alpha, Button, Link, Snackbar, SnackbarContent, Typography, useTheme } from '@mui/material';
import { getTranslucentSurfaceStyles } from 'components/theme/theme';
import React, { FunctionComponent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import useLocalStorage from 'use-local-storage';

interface CookieNoticeProps {}

const CookieNotice: FunctionComponent<React.PropsWithChildren<CookieNoticeProps>> = () => {
  const [accepted, setAccepted] = useLocalStorage('cookie-notice-accepted', false);
  const theme = useTheme();
  const translucentSurfaceStyles = getTranslucentSurfaceStyles(theme);

  return (
    <Snackbar
      id="cookie-notice"
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      open={!accepted}
      sx={{
        width: {
          xs: 'calc(100% - 24px)',
          sm: 'min(720px, calc(100% - 32px))',
        },
        '& .MuiSnackbarContent-root': {
          ...translucentSurfaceStyles,
          width: '100%',
          overflow: 'hidden',
          alignItems: {
            xs: 'flex-start',
            sm: 'center',
          },
          gap: {
            xs: 1.5,
            sm: 2,
          },
          px: {
            xs: 2,
            sm: 2.5,
          },
          py: {
            xs: 1.5,
            sm: 1.75,
          },
          borderRadius: `${theme.shape.borderRadius}px`,
          border: `1px solid ${alpha(
            theme.palette.mode === 'light' ? theme.palette.common.black : theme.palette.common.white,
            theme.palette.mode === 'light' ? 0.1 : 0.12
          )}`,
          color: theme.palette.text.primary,
        },
        '& .MuiSnackbarContent-message': {
          flex: '1 1 auto',
          minWidth: 0,
          padding: 0,
        },
        '& .MuiSnackbarContent-action': {
          alignSelf: {
            xs: 'stretch',
            sm: 'center',
          },
          margin: 0,
          paddingLeft: 0,
        },
      }}
    >
      <SnackbarContent
        message={
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              lineHeight: 1.5,
              '& a': {
                color: theme.palette.primary.main,
                fontWeight: 700,
                textDecorationColor: alpha(theme.palette.primary.main, 0.35),
              },
            }}
          >
            This website stores cookies on your computer. These cookies are used to collect information about how you
            interact with this website and allow us to remember you. We use this information in order to improve and
            customize your browsing experience and for analytics and metrics about our visitors both on this website and
            other media. To find out more about the cookies we use, see our{' '}
            <Link to="/privacy-policy" component={RouterLink} underline="always">
              Privacy Policy.
            </Link>
          </Typography>
        }
        action={
          <Button
            id="cookie-notice-accept"
            data-cy="cookie-notice-accept"
            color="primary"
            size="small"
            onClick={() => setAccepted(true)}
            sx={{
              alignSelf: 'center',
              minWidth: 104,
              whiteSpace: 'nowrap',
            }}
            variant="contained"
          >
            Accept
          </Button>
        }
      />
    </Snackbar>
  );
};

export default CookieNotice;
